import { useRef, useState } from 'react';
import { ListMetaNetworkParams } from '../../list-conductor/list-meta-data';
import { SortDirection } from '../../list-conductor/sort';
import { PaginatedResponse } from '../../request/hooks/useQuery';

export function useInMemoryFilteredList<T>(
  dataSource$: () => Promise<T[]>,
  filterMethod: (item: T, params: ListMetaNetworkParams) => boolean,
  initialData?: T[],
  itemSelectionQuery?: (item: T) => boolean,
  getSortValue?: (item: T, field: string) => string | number,
  isExpandingList?: boolean,
  itemSelectionIdentifier?: keyof T,
  validateAndAddToSelection?: (item: T) => boolean,
  bulkSelectionActionHandler?: (items: Set<string | number>) => void,
  backgroundDataSource$?: () => Promise<T[]>
) {
  const dataRef = useRef<T[] | undefined>(initialData);
  const pageDataRef = useRef<T[] | undefined>(undefined);
  const [selectedItems, setSelectedItems] = useState<Set<string | number>>(new Set([]));
  const backgroundDataRef = useRef<T[] | undefined>(initialData);
  const [filteredList, setFilteredList] = useState<T[]>([]);
  const [filteredListSelectedCount, setFilteredListSelectedCount] = useState(0);
  const [selectedCount, setSelectedCount] = useState(0);

  const getFilteredAndPaginatedData = (d: T[], params: ListMetaNetworkParams) => {
    const page = (params?.page as number) || 0;
    const limit = (params?.limit as number) || undefined;

    const newList = d.filter(item => {
      return filterMethod(item, params);
    });

    ((params?.sort || []) as { field: string; order: SortDirection }[]).forEach(
      ({ field, order }) => {
        newList.sort((a, b) => {
          const f1 = typeof getSortValue === 'function' ? getSortValue(a, field) : a;
          const f2 = typeof getSortValue === 'function' ? getSortValue(b, field) : b;

          if (f1 < f2) {
            return order === 'ASC' ? -1 : 1;
          }

          if (f1 > f2) {
            return order === 'ASC' ? 1 : -1;
          }

          return 0;
        });
      }
    );

    setSelectedCountOnFilteredList(newList);
    setFilteredList(newList);

    const total = newList.length;

    if (!limit) {
      return { items: newList, total };
    }

    const start = isExpandingList ? 0 : page * limit;
    const end = (page + 1) * limit;
    pageDataRef.current = newList.slice(start, end);
    return { items: newList.slice(start, end), total };
  };

  const setSelectedCountOnFilteredList = (newFilteredList: T[]) => {
    if (typeof itemSelectionQuery === 'function') {
      setFilteredListSelectedCount(newFilteredList.filter(itemSelectionQuery).length);
    }
  };

  const setSelectedCountForList = (list: T[]) => {
    if (typeof itemSelectionQuery === 'function') {
      setSelectedCount(list.filter(itemSelectionQuery).length);
    }
  };

  const fetch = async (params?: ListMetaNetworkParams): Promise<PaginatedResponse<T[]>> => {
    const listNetworkParams = params as ListMetaNetworkParams;
    const page = (listNetworkParams?.page as number) || 0;

    if (dataRef.current) {
      setSelectedCountForList(dataRef.current);
      const { items, total } = getFilteredAndPaginatedData(dataRef.current, listNetworkParams);

      return Promise.resolve({
        items,
        responseMetaData: {
          page,
          count: total
        }
      });
    }

    const d = await dataSource$();

    dataRef.current = d;
    setSelectedCountForList(dataRef.current);

    const { items, total } = getFilteredAndPaginatedData(d, listNetworkParams);

    return {
      items,
      responseMetaData: {
        page,
        count: total
      }
    };
  };

  const fetchBackgroundData = async (
    params?: ListMetaNetworkParams
  ): Promise<PaginatedResponse<T[]>> => {
    if (!backgroundDataSource$) {
      return Promise.reject(new Error('Failed to resolve data source'));
    }

    const listNetworkParams = params as ListMetaNetworkParams;
    const page = (listNetworkParams?.page as number) || 0;

    if (backgroundDataRef.current) {
      const { items, total } = getFilteredAndPaginatedData(
        backgroundDataRef.current,
        listNetworkParams
      );

      return Promise.resolve({
        items,
        responseMetaData: {
          page,
          count: total
        }
      });
    }

    const d = await backgroundDataSource$();

    backgroundDataRef.current = d;

    const { items, total } = getFilteredAndPaginatedData(d, listNetworkParams);

    return {
      items,
      responseMetaData: {
        page,
        count: total
      }
    };
  };

  const reset = () => {
    dataRef.current = undefined;
    backgroundDataRef.current = undefined;
  };

  const updateMemoryData = (d: T[] | undefined) => {
    dataRef.current = d;
  };

  const onItemSelectionChange = (item: T, selected: boolean) => {
    if (typeof validateAndAddToSelection !== 'undefined' && !validateAndAddToSelection(item)) {
      return;
    }

    setSelectedItems(prev => {
      if (itemSelectionIdentifier) {
        if (selected) {
          prev.add(item[itemSelectionIdentifier] as string);
        } else {
          prev.delete(item[itemSelectionIdentifier] as string);
        }
      }
      return new Set(prev);
    });
  };

  const onAllItemsSelectionChange = (selected: boolean) => {
    if (selected) {
      setSelectedItems(prev => {
        if (itemSelectionIdentifier) {
          filteredList.forEach(data => prev.add(data[itemSelectionIdentifier] as string));
        }

        return new Set(prev);
      });
    } else {
      setSelectedItems(prev => {
        if (itemSelectionIdentifier) {
          filteredList.forEach(data => prev.delete(data[itemSelectionIdentifier] as string));
        }

        return new Set(prev);
      });
    }
  };

  const onAllInPageSelectionChange = (selected: boolean) => {
    if (selected) {
      setSelectedItems(prev => {
        if (itemSelectionIdentifier) {
          pageDataRef.current?.forEach(data => prev.add(data[itemSelectionIdentifier] as string));
        }

        return new Set(prev);
      });
    } else {
      setSelectedItems(prev => {
        if (itemSelectionIdentifier) {
          pageDataRef.current?.forEach(data =>
            prev.delete(data[itemSelectionIdentifier] as string)
          );
        }

        return new Set(prev);
      });
    }
  };

  const onBulkActionCTAClick = async () => {
    if (typeof bulkSelectionActionHandler === 'function') {
      bulkSelectionActionHandler(selectedItems);
    }
  };

  const resetSelectedItems = () => {
    setSelectedItems(new Set([]));
  };

  return {
    fetch,
    fetchBackgroundData,
    reset,
    data: dataRef.current,
    filteredList,
    selectedCount,
    filteredListSelectedCount,
    updateMemoryData,
    onItemSelectionChange,
    onAllItemsSelectionChange,
    onAllInPageSelectionChange,
    selectedItems,
    onBulkActionCTAClick,
    resetSelectedItems
  };
}
